import type { Context } from '@montugroup/data-collection';
import { emitUIEvent } from '@montugroup/data-collection';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import type { InputProps, TooltipProps } from '@mui/material';
import { Box, Input, Stack, Tooltip, tooltipClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { FC } from 'react';

import { Button } from '~/components/atoms/Button';
import { getDDMaskClass } from '~/utils/datadogMaskClass';

const StyledInput = styled(Input, {
  shouldForwardProp: (prop) => prop !== 'hasTopBorder',
})<{ hasTopBorder: boolean }>(({ theme, hasTopBorder }) => ({
  width: theme.spacing(5),
  minWidth: theme.spacing(5),
  px: 1,
  '&.Mui-disabled::before': {
    borderBottomStyle: 'solid',
    borderBottomColor: theme.palette.grey[300],
  },
  '&.Mui-disabled': {
    borderColor: theme.palette.grey[300],
  },
  '& input.Mui-disabled': {
    color: theme.palette.grey[500],
    WebkitTextFillColor: theme.palette.grey[500],
  },
  borderTop: hasTopBorder ? `1px solid ${theme.palette.grey[500]}` : 'none',
  input: {
    textAlign: 'center',
  },
  /* Chrome, Safari, Edge, Opera */
  'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
    appearance: 'none',
    margin: 0,
  },

  /* Firefox */
  'input[type=number]': {
    appearance: 'textfield',
  },
}));

const StyledToolTip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    arrow
    classes={{ popper: className }}
    enterTouchDelay={0}
    placement="top"
    slotProps={{
      popper: {
        popperOptions: {
          modifiers: [
            {
              name: 'computeStyles',
              options: {
                adaptive: false,
              },
            },
          ],
        },
      },
    }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.grey[900],
    color: theme.palette.common.white,
    fontSize: theme.typography.body2.fontSize,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.grey[900],
  },
}));

type QuantityAdjustProps = Pick<InputProps, 'disabled' | 'onBlur'> & {
  maxValue?: number;
  minValue?: number;
  onChange: (newValue: number | '', oldValue: number | '') => void;
  step?: number;
  value: number | '';
  variant?: 'text' | 'outlined' | 'contained';
  testID?: string;
  removePIIMask?: boolean;
} & (
    | { id: string; context?: Context }
    | {
        id?: never;
        context?: never;
      }
  );

const testIdBase = 'quantity-picker';

export const QuantityPicker: FC<QuantityAdjustProps> = ({
  disabled,
  maxValue,
  minValue,
  onChange,
  step = 1,
  value,
  variant = 'outlined',
  testID = '',
  id,
  context,
  removePIIMask,
  onBlur,
}) => {
  const formattedValue = value !== '' ? value : 0;

  const stepPrecision = (step + '').replace('.', '').length - 1;
  const decreasedQuantity =
    minValue !== undefined
      ? Math.max(minValue, Number((formattedValue - step).toFixed(stepPrecision)))
      : Number((formattedValue - step).toFixed(stepPrecision));
  const increasedQuantity =
    maxValue !== undefined
      ? Math.min(maxValue, Number((formattedValue + step).toFixed(stepPrecision)))
      : Number((formattedValue + step).toFixed(stepPrecision));

  const testIdPrefix = testID ? `${testID}-${testIdBase}` : testIdBase;

  const formatInput = (val: number) => {
    if (!stepPrecision) {
      return Math.round(val);
    }
    return Number(val.toFixed(stepPrecision));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value !== '' ? formatInput(Number(e.target.value)) : '';

    handleOnChangeSnowplowEvent(newValue, value);
  };

  const handleOnChangeSnowplowEvent = (newQuantity: number | '', prevQuantity: number | '') => {
    // Track the quantity update if a context is provided
    if (newQuantity !== '' && prevQuantity !== newQuantity && context) {
      emitUIEvent(
        {
          component_name: 'QuantityPicker',
          id: id,
          event_name: newQuantity > (prevQuantity === '' ? 0 : prevQuantity) ? 'add_quantity' : 'reduce_quantity',
          value: String(newQuantity ?? 0),
        },
        [context],
      );
    }
    onChange(newQuantity, value);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e?.target) {
      const newValue = e.target.value !== '' ? formatInput(Number(e.target.value)) : '';

      if (minValue !== undefined && newValue !== '' && newValue < minValue) {
        handleOnChangeSnowplowEvent(minValue, value);
      } else if (maxValue !== undefined && newValue !== '' && newValue > maxValue) {
        handleOnChangeSnowplowEvent(maxValue, value);
      } else {
        handleOnChangeSnowplowEvent(newValue, value);
      }
    }

    onBlur?.(e);
  };

  return (
    <Stack direction="row" data-testid={`${testIdPrefix}-container`}>
      <StyledToolTip
        title={
          !disabled && minValue !== undefined && formattedValue <= minValue ? 'Minimum quantity reached.' : undefined
        }
      >
        {/* Use span to ensure tooltip appears when child is disabled. */}
        <Box component="span">
          <Button
            aria-label={`Decrease quantity by ${step}`}
            data-sp-button-label="Decrease quantity"
            disabled={disabled || (minValue !== undefined ? formattedValue <= minValue : undefined)}
            variant={variant}
            onClick={() => handleOnChangeSnowplowEvent(decreasedQuantity, value)}
            sx={{ px: 1, borderTopRightRadius: '0!important', borderBottomRightRadius: '0!important', minWidth: 0 }}
          >
            <Remove />
          </Button>
        </Box>
      </StyledToolTip>

      <StyledInput
        type="number"
        inputProps={{
          'data-testid': `${testIdPrefix}-input`,
        }}
        aria-label="Quantity"
        value={value}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        hasTopBorder={variant !== 'text'}
        disabled={disabled}
        className={removePIIMask ? getDDMaskClass('ALLOW') : ''}
      />

      <StyledToolTip
        title={
          !disabled && maxValue !== undefined && formattedValue >= maxValue ? 'Maximum quantity reached.' : undefined
        }
      >
        {/* Use span to ensure tooltip appears when child is disabled. */}
        <Box component="span">
          <Button
            aria-label={`Increase quantity by ${step}`}
            data-sp-button-label="Increase quantity"
            variant={variant}
            disabled={disabled || (maxValue !== undefined ? formattedValue >= maxValue : undefined)}
            onClick={() => handleOnChangeSnowplowEvent(increasedQuantity, value)}
            sx={{ px: 1, borderTopLeftRadius: '0!important', borderBottomLeftRadius: '0!important', minWidth: 0 }}
          >
            <Add />
          </Button>
        </Box>
      </StyledToolTip>
    </Stack>
  );
};
